import React, { useState } from 'react';
import { Pulldown, Visibility, CheckCircle, Excluded } from 'jbc-front/components/icons';
import { useTranslation } from 'react-i18next';
import styles from './ChartTable.scss';

export const chartSorter = (a, b, isAscending = false, sortBy = 'percentage-label') => {
  if (sortBy === 'percentage-label' || sortBy === 'value-label') {
    if (!isAscending) return a.number >= b.number ? -1 : 1;
    return a.number >= b.number ? 1 : -1;
  }
  if (sortBy === 'name-label') {
    if (a.name && b.name) {
      if (!isAscending) return a.name.localeCompare(b.name);
      return b.name.localeCompare(a.name);
    }
    if (a.name === null) return isAscending ? -1 : 1;
    if (b.name === null) return !isAscending ? -1 : 1;
  }
  if (sortBy === 'expenses-label') {
    if (!isAscending) return a.isConfirmed === b.isConfirmed ? 0 : a.isConfirmed ? -1 : 1;
    return a.isConfirmed === b.isConfirmed ? 0 : a.isConfirmed ? 1 : -1;
  }
  return 0;
};

export const sumNumber = (chartData, exceptionFilter = []) =>
  // calculate sum of count base on enabled label
  chartData.filter(item => !exceptionFilter.includes(item.id.toString())).reduce((accumulator, object) => accumulator + object.number, 0);

export const numberFormat = (num, decimalPlaces = 0) =>
  parseFloat(num)
    .toFixed(decimalPlaces)
    .replace('.', ',')
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

export const ChartTable = ({ chartData, nameTableLabel, valueTableLabel, disabled, setdisabled, valueDecimal = 0, maxHeight, expensesTable = false }) => {
  const { t } = useTranslation();
  const [isAscending, setisAscending] = useState(false);
  const [sortSelected, setsortSelected] = useState('percentage-label');

  const disabledHandler = e => {
    const id = e.currentTarget.id.split('-');
    // toggle disabled/enabled for data row
    if (!disabled.includes(id[id.length - 1])) {
      if (disabled.length < chartData.length - 1) {
        setdisabled(array => [...array, id[id.length - 1]]);
      }
    } else {
      setdisabled(disabled.filter(item => item !== id[id.length - 1]));
    }
  };

  const sortClickHandler = e => {
    const id = e.currentTarget.id;
    // change sortSelected when click on table header
    if (id === sortSelected) {
      setisAscending(!isAscending);
    } else {
      setsortSelected(id);
      setisAscending(false);
    }
  };

  return (
    <div className={styles.tableContainer} style={{ maxHeight }}>
      <table className={styles.chartTable}>
        <thead>
          <tr>
            <th
              className={styles.tableHover}
              onClick={() => {
                setdisabled([]);
              }}
            >
              <div className={styles.hoverable}>
                <Visibility size={13} />
              </div>
            </th>
            <th
              id="name-label"
              className={styles.tableHover}
              onClick={e => {
                sortClickHandler(e);
              }}
            >
              <div className={styles.filterWrapper}>
                <div style={{ width: '100%' }}>{nameTableLabel}</div>{' '}
                <div
                  className={`${styles.hoverable} ${styles.sortable} ${sortSelected === 'name-label' ? styles.active : ''} ${
                    isAscending ? styles.ascending : ''
                  }`}
                >
                  <Pulldown size={7} />
                </div>
              </div>
            </th>
            <th
              id="value-label"
              className={styles.tableHover}
              onClick={e => {
                sortClickHandler(e);
              }}
            >
              <div className={styles.filterWrapper}>
                <div style={{ width: '100%' }}>{valueTableLabel}</div>{' '}
                <div
                  className={`${styles.hoverable} ${styles.sortable} ${sortSelected === 'value-label' ? styles.active : ''} ${
                    isAscending ? styles.ascending : ''
                  }`}
                >
                  <Pulldown size={7} />
                </div>
              </div>
            </th>
            <th
              id="percentage-label"
              className={`${styles.tableHover} ${styles.percentage}`}
              onClick={e => {
                sortClickHandler(e);
              }}
            >
              <div className={styles.filterWrapper}>
                <div style={{ width: '100%' }}>%</div>{' '}
                <div
                  className={`${styles.hoverable} ${styles.sortable} ${sortSelected === 'percentage-label' ? styles.active : ''} ${
                    isAscending ? styles.ascending : ''
                  }`}
                >
                  <Pulldown size={7} />
                </div>
              </div>
            </th>
            {expensesTable && (
              <th
                id="expenses-label"
                className={styles.tableHover}
                onClick={e => {
                  sortClickHandler(e);
                }}
              >
                <div className={styles.filterWrapper}>
                  <div style={{ width: '100%' }}>{'Conf.'}</div>{' '}
                  <div
                    className={`${styles.hoverable} ${styles.sortable} ${sortSelected === 'expenses-label' ? styles.active : ''} ${
                      isAscending ? styles.ascending : ''
                    }`}
                  >
                    <Pulldown size={7} />
                  </div>
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {/* Enabled Data Section */}
          {[...chartData]
            .sort((a, b) => chartSorter(a, b, isAscending, sortSelected))
            .filter(data => !disabled.includes(data.id.toString()))
            .map(data => (
              <tr
                id={`active-${data.id}`}
                onClick={e => {
                  disabledHandler(e);
                }}
                key={data.id}
                className={`${styles.tableHover} ${disabled.length < chartData.length - 1 ? '' : styles.lastOne}`}
              >
                <td>
                  <div
                    className={`${styles.circle}`}
                    style={{
                      background: disabled.includes(data.id.toString()) ? 'transparent' : data.color,
                      border: `2px solid${data.color}`
                    }}
                  />
                </td>
                <td id="name" title={data.name ? data.name : t('features.Dashbord.components.EmployeeCategory.not_assigned')}>
                  {data.name ? data.name : t('features.Dashbord.components.EmployeeCategory.not_assigned')}
                </td>
                <td id="value">{numberFormat(data.number, valueDecimal)}</td>
                <td id="percentage">{(data.number / sumNumber(chartData, disabled) * 100).toFixed(2).replace('.', ',')}</td>
                {expensesTable && (
                  <td id={`confirm-status-${data.isConfirmed ? 'confirmed' : 'unconfirmed'}`} className={styles.confirm}>
                    <a href={data.link}>
                      {data.isConfirmed ? (
                        <CheckCircle id="confirmed" size={16} style={{ color: 'green' }} />
                      ) : (
                        <Excluded id="unconfirmed" size={16} style={{ color: 'orange' }} />
                      )}
                    </a>
                  </td>
                )}
              </tr>
            ))}

          {/* Disabled Data Section */}
          {[...chartData]
            .sort((a, b) => chartSorter(a, b, isAscending, sortSelected))
            .filter(data => disabled.includes(data.id.toString()))
            .map(data => (
              <tr
                id={`inactive-${data.id}`}
                onClick={e => {
                  disabledHandler(e);
                }}
                key={data.id}
                className={`${styles.tableHover} ${styles.disabled}`}
              >
                <td>
                  <div
                    className={styles.circle}
                    style={{
                      background: disabled.includes(data.id.toString()) ? 'transparent' : data.color,
                      border: `2px solid${data.color}`
                    }}
                  />
                </td>
                <td id="name" title={data.name ? data.name : t('features.Dashbord.components.EmployeeCategory.not_assigned')}>
                  {data.name ? data.name : t('features.Dashbord.components.EmployeeCategory.not_assigned')}
                </td>
                <td id="value">{numberFormat(data.number, valueDecimal)}</td>
                <td id="percentage">-</td>
                {expensesTable && (
                  <td id={`confirm-status-${data.isConfirmed ? 'confirmed' : 'unconfirmed'}`} className={styles.confirm}>
                    <a href={data.link}>
                      {data.isConfirmed ? <CheckCircle size={16} style={{ color: 'green' }} /> : <Excluded size={16} style={{ color: 'orange' }} />}
                    </a>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default ChartTable;
